import React, { FC, Fragment, useEffect, useMemo, useReducer } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  AppBar,
  Grid,
  Button,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { ACCESS_KEY } from 'app-constants';

import {
  GetPublicContentQuery,
  useGetClientsCapabilitiesQuery,
  useGetPublicContentQuery,
  ListPublicSocialConnectionsQuery,
  useListPublicSocialConnectionsQuery,
} from 'api/public';
import { ClientLogo, Dimensions, Layouts, Loaders } from 'components';
import { useGlobalClient, useNotify } from 'hooks';
import { Events } from 'analytics';
import * as Links from 'Links';
import {
  CapabilitiesContext,
  ContentProvider,
  DimensionsProvider,
  QueryVariablesProvider,
} from 'providers';
import * as LayoutComponents from 'startup/public/Components';
import { FACEBOOK_APP_ID } from 'settings';

import { getOpenGraphTags, getPublicLayout, isTextOnly } from '../helpers';
import PublicShareCard from '../partials/PublicShareCard';
import { initialPublicPostState, PublicPostContext, publicPostReducer } from '../PublicPostContext';
import { hasCapability } from 'helpers';

const useStyles = makeStyles(theme => ({
  logo: {
    display: 'flex',
    alignItems: 'center',
    paddingRight: theme.spacing(2),
  },
  label: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
  },
  button: {
    borderRadius: 16,
  },
  toolbar: {
    flexGrow: 1,
    justifyContent: 'space-between',
  },
  container: {
    marginTop: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(0),
    },
  },
  mobileLogin: {
    backgroundColor: theme.palette.common.white,
    color: theme.palette.grey[500],
    fontWeight: 800,
    borderRadius: 16,
    '&:hover': {
      color: theme.palette.common.white,
      fontWeight: 800,
    },
  },
}));

interface IRouteParams {
  content_id: string;
}

const PublicPermalink: FC = () => {
  const classes = useStyles();
  const notify = useNotify();
  const client = useGlobalClient();
  const { t } = useTranslation();
  const { content_id } = useParams<IRouteParams>();
  const theme = useTheme();
  const isDesktopLayout = useMediaQuery(theme.breakpoints.up('md'));

  const authToShareSpacing = isDesktopLayout ? 3 : 0;

  const [state, dispatch] = useReducer(publicPostReducer, initialPublicPostState);

  function onSuccess(publicContentData: GetPublicContentQuery) {
    dispatch({ type: 'setContent', value: publicContentData?.getPublicContent });
  }

  const { data, isLoading } = useGetPublicContentQuery(
    { content_id, client_id: client.id },
    {
      onError: notify.queryError,
      onSuccess: onSuccess,
    }
  );

  const { data: capabilitiesData, isLoading: isCapabilitiesLoading } =
    useGetClientsCapabilitiesQuery(
      {
        client_id: client.id,
      },
      { onSuccess: onCapabilitiesSuccess }
    );

  const capabilitiesList = capabilitiesData?.getClientsCapabilities || [];

  const capabilities = useMemo(() => {
    return capabilitiesList.map(capability => {
      const { active, capability_string } = capability;
      if (active) {
        return capability_string;
      }
      return null;
    });
  }, [capabilitiesList]);

  const hasAuthToShare = hasCapability(capabilities, `Sharing.Function.AuthToShare`);

  const accessKey = localStorage.getItem(ACCESS_KEY);

  function onListSocialSuccess(data: ListPublicSocialConnectionsQuery) {
    const { listPublicSocialConnections } = data;
    const { access_key } = listPublicSocialConnections;

    localStorage.setItem(ACCESS_KEY, access_key);
  }

  const variables = accessKey !== 'null' ? { access_key: accessKey } : {};

  const { refetch, isLoading: isListPublicSocialLoading } = useListPublicSocialConnectionsQuery(
    variables,
    {
      onError: notify.queryError,
      onSuccess: onListSocialSuccess,
      enabled: false,
    }
  );

  function onCapabilitiesSuccess() {
    if (hasAuthToShare) {
      void refetch();
    }
  }

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('es_source')) {
      window?.analytics?.track(Events.RepostClicked, {
        referral_source: searchParams.get('es_source'),
      });
    }

    window?.analytics?.track(Events.PublicPostImpression);
  }, []);

  if (isLoading || isCapabilitiesLoading || isListPublicSocialLoading) {
    return <Loaders.Standard />;
  }

  // Avoid showing the visitor an empty page
  const isBlankLayout = !client.public_post_details && isTextOnly(data?.getPublicContent);

  if (!data?.getPublicContent || isBlankLayout) {
    return <LayoutComponents.AuthLayout />;
  }

  const { title, description, imageUrl, imageHeight, imageWidth } = getOpenGraphTags(
    data.getPublicContent
  );

  function handleTracking(property: 'customer' | 'everyonesocial') {
    window?.analytics?.track(Events.PublicPostLogoClicked, {
      customer: property === 'customer',
      everyonesocial: property === 'everyonesocial',
    });
  }

  function onClickLogin() {
    location.assign('/');
  }

  function renderContent() {
    const { getPublicContent } = data;
    const ContentComponent = getPublicLayout(getPublicContent);

    return (
      <QueryVariablesProvider value={{ content_id }}>
        <ContentProvider value={getPublicContent}>
          <Dimensions>
            {dimensions => (
              <DimensionsProvider value={dimensions}>
                <ContentComponent />
              </DimensionsProvider>
            )}
          </Dimensions>
        </ContentProvider>
      </QueryVariablesProvider>
    );
  }

  function renderAuthToShare() {
    return (
      <Layouts.App1Column
        main={
          <Grid
            container
            spacing={authToShareSpacing}
            justify="center"
            className={classes.container}
          >
            <Grid item xs={12} md={5}>
              {renderContent()}
            </Grid>
            <Grid item xs={12} md={7}>
              <PublicShareCard />
            </Grid>
          </Grid>
        }
      ></Layouts.App1Column>
    );
  }

  function renderNoAuthToShare() {
    return (
      <Layouts.App1Column
        mobileSize={10}
        tabletSize={8}
        desktopSize={6}
        main={<Layouts.AppMain1Column main={renderContent()} />}
      />
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:height" content={imageHeight} />
        <meta property="og:image:width" content={imageWidth} />
        <meta property="fb:app_id" content={FACEBOOK_APP_ID} />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:site" content="@everyonesocial" />
      </Helmet>
      <AppBar>
        <Toolbar className={classes.toolbar}>
          <Typography
            onClick={() => handleTracking('customer')}
            className={classes.logo}
            component={Links.Index}
            color="inherit"
          >
            <ClientLogo client={client} disableMargin height={24} />
          </Typography>
          {hasAuthToShare && (
            <Typography className={classes.label} variant="h4">
              {t('sharing:shareToYourNetworks')}
            </Typography>
          )}
          <Button
            className={isDesktopLayout ? classes.button : classes.mobileLogin}
            variant="contained"
            onClick={onClickLogin}
          >
            {t('components:login/signup')}
          </Button>
        </Toolbar>
      </AppBar>
      <CapabilitiesContext.Provider value={capabilities}>
        <PublicPostContext.Provider value={{ state, dispatch }}>
          <div>{hasAuthToShare ? renderAuthToShare() : renderNoAuthToShare()}</div>
        </PublicPostContext.Provider>
      </CapabilitiesContext.Provider>
    </Fragment>
  );
};

export default PublicPermalink;
